import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Flex, Grid, Text, VStack, toaster, useT } from '@minetrace/ui'
import { motion } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import {
  LuArrowLeft as ArrowLeft,
  LuBuilding2 as Building2,
  LuDatabase as Database,
  LuMountain as Mountain,
  LuFileText as FileText,
  LuUsers as Users,
  LuShield as Shield,
  LuFlaskConical as Flask,
  LuChartBar as BarChart,
  LuClipboardCheck as ClipboardCheck,
  LuLandmark as Landmark,
  LuAward as Award,
  LuSend as Send,
  LuFolderOpen,
  LuLayoutDashboard as LayoutDashboard,
  LuFilePen as FileSignature,
  LuScale as Scale,
  LuReceipt as Receipt,
  LuKeyRound as KeyRound,
  LuCircleCheck as CircleCheck,
  LuCircleX as CircleX,
  LuTriangleAlert as TriangleAlert,
  LuMapPin as MapPin,
} from 'react-icons/lu'
import { AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps'
import { KMap, FitBounds, useMap } from '../../lib/googleMaps'
import { ComponentPin } from '../../lib/mapMarkers'
import { utmToLatLng, getUtmZoneFromGeoJson, isValidLatLng } from '../../utils/utmConverter'
import { proveedoresService } from '../../api/services'
import DocumentoDescarga from '../../components/DocumentoDescarga'
import apiClient from '../../api/client'
import type { ProveedorResponse, SupplierApprovalListItemResponse, ShareholderResponse, RegistrationStatusChangeResponse } from '../../api/models/response/proveedor'
import { brand, themeColors } from '../../theme'
import {
  MINING_CATEGORIES,
  MINING_RIGHT_STATUSES,
  INCOME_TAX_CATEGORIES,
  PRODUCTION_PERIODS,
  enumLabel,
} from '../../data/enums'
import ubigeoData from '../../data/ubigeo.json'
import { formatDateTime, formatDate } from '../../lib/dates'
import { etiquetaSituacionRecpo } from '../../lib/recpo'

const MotionBox = motion.create(Box)

type UbigeoJson = Record<string, {
  name: string
  provinces: Record<string, {
    name: string
    districts: Record<string, { name: string }>
  }>
}>

const ubigeo = ubigeoData as UbigeoJson

function resolveUbigeo(code: string | null): string {
  if (!code || code.length < 2) return '-'
  const depto = ubigeo[code.substring(0, 2)]
  if (!depto) return code
  if (code.length < 4) return depto.name
  const prov = depto.provinces[code.substring(0, 4)]
  if (!prov) return depto.name
  if (code.length < 6) return `${depto.name} / ${prov.name}`
  const dist = prov.districts[code]
  if (!dist) return `${depto.name} / ${prov.name}`
  return `${depto.name} / ${prov.name} / ${dist.name}`
}

function InfoField({ label, value }: { label: string; value: string }) {
  const colors = themeColors
  return (
    <Box>
      <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.12em" fontWeight="bold" color={colors.textMuted}>
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="500" color={colors.text} mt={0.5}>
        {value || '-'}
      </Text>
    </Box>
  )
}

function SectionCard({ icon: Icon, title, action, children, delay = 0 }: {
  icon: typeof Building2
  title: string
  action?: React.ReactNode
  children: React.ReactNode
  delay?: number
}) {
  const colors = themeColors
  return (
    <MotionBox
      bg={colors.bgCard}
      border="1px solid"
      borderColor={colors.border}
      borderRadius="2xl"
      overflow="hidden"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay }}
    >
      <Flex p={5} borderBottom="1px solid" borderColor={colors.border} alignItems="center" gap={2} justifyContent="space-between">
        <Flex alignItems="center" gap={2} minW={0}>
          <Box display="flex" alignItems="center" justifyContent="center" w="28px" h="28px" borderRadius="lg" bg={`${brand.green}12`} color={brand.green} flexShrink={0}>
            <Icon size={16} />
          </Box>
          <Text fontSize="md" fontWeight="bold" color={colors.text} truncate>{title}</Text>
        </Flex>
        {action}
      </Flex>
      <Box p={6}>{children}</Box>
    </MotionBox>
  )
}

function EmptyState({ label }: { label: string }) {
  const colors = themeColors
  return (
    <Flex direction="column" alignItems="center" justifyContent="center" py={6} gap={2}>
      <Box color={colors.textDimmed}><FileText size={22} /></Box>
      <Text fontSize="sm" color={colors.textMuted}>{label}</Text>
    </Flex>
  )
}

type LL = google.maps.LatLngLiteral

function parseGeoJson(geoJsonStr: string | null): GeoJSON.Geometry | null {
  if (!geoJsonStr) return null
  try {
    return JSON.parse(geoJsonStr) as GeoJSON.Geometry
  } catch {
    return null
  }
}

// Aplana una geometría GeoJSON (Polygon / MultiPolygon) en anillos de
// LatLngLiteral. GeoJSON es [lng, lat]; Google es {lat, lng}.
function geometryRings(geometry: GeoJSON.Geometry | null): LL[][] {
  if (!geometry) return []
  const toRing = (ring: GeoJSON.Position[]): LL[] =>
    ring.map((c) => ({ lat: c[1] as number, lng: c[0] as number })).filter((p) => isValidLatLng(p.lat, p.lng))
  if (geometry.type === 'Polygon') return geometry.coordinates.map(toRing)
  if (geometry.type === 'MultiPolygon') return geometry.coordinates.flatMap((poly) => poly.map(toRing))
  return []
}

/**
 * Polígono GeoJSON imperativo sobre `google.maps.Polygon` (la base de
 * googleMaps solo expone Polyline). Mismo patrón de ciclo de vida que
 * `lib/googleMaps/Polyline.tsx`.
 */
function GeoJsonPolygon({ rings, color, fillOpacity }: { rings: LL[][]; color: string; fillOpacity: number }) {
  const map = useMap()
  const polyRef = useRef<google.maps.Polygon | null>(null)
  useEffect(() => {
    if (!map) return
    const poly = new google.maps.Polygon()
    poly.setMap(map)
    polyRef.current = poly
    return () => {
      poly.setMap(null)
      polyRef.current = null
    }
  }, [map])
  useEffect(() => {
    polyRef.current?.setOptions({
      paths: rings,
      strokeColor: color,
      strokeWeight: 2,
      fillColor: color,
      fillOpacity,
      clickable: false,
    })
  }, [rings, color, fillOpacity])
  return null
}

function registrationStatusBadge(status: number) {
  const map: Record<number, { label: string; bg: string; color: string }> = {
    0: { label: 'Borrador', bg: 'rgba(107, 114, 128, 0.15)', color: '#857b71' },
    1: { label: 'En Evaluación', bg: 'rgba(212, 154, 42, 0.15)', color: '#a8740e' },
    2: { label: 'Observado', bg: 'rgba(200, 68, 47, 0.15)', color: '#b23a26' },
    3: { label: 'Aprobado', bg: 'rgba(42, 138, 135, 0.15)', color: '#1d6664' },
  }
  return map[status] ?? map[0]!
}

// Badge de formalización por Derecho Minero: 2/3 Formalizado (verde), 1 En vías (ámbar).
function formalizationBadge(status: number | null): { label: string; bg: string; color: string } | null {
  if (status === 2 || status === 3) return { label: 'Formalizado', bg: 'rgba(42, 138, 135, 0.15)', color: '#1d6664' }
  if (status === 1) return { label: 'En proceso de formalización', bg: 'rgba(212, 154, 42, 0.15)', color: '#a8740e' }
  return null
}

// Badge de validación del CUL del accionista.
function shareholderBadge(status: number): { bg: string; color: string; icon: React.ReactNode } {
  if (status === 1) return { bg: 'rgba(42, 138, 135, 0.15)', color: '#1d6664', icon: <CircleCheck size={12} /> }
  if (status === 2 || status === 4) return { bg: 'rgba(200, 68, 47, 0.15)', color: '#b23a26', icon: <CircleX size={12} /> }
  if (status === 0) return { bg: 'rgba(107, 114, 128, 0.15)', color: '#857b71', icon: <TriangleAlert size={12} /> }
  return { bg: 'rgba(212, 154, 42, 0.15)', color: '#a8740e', icon: <TriangleAlert size={12} /> }
}

function StatusPill({ label, bg, color, icon }: { label: string; bg: string; color: string; icon?: React.ReactNode }) {
  return (
    <Flex as="span" display="inline-flex" alignItems="center" gap={1} px={2} py={0.5} borderRadius="full" fontSize="10px" fontWeight="bold" textTransform="uppercase" letterSpacing="0.03em" bg={bg} color={color} flexShrink={0}>
      {icon}{label}
    </Flex>
  )
}

// ── KPI strip ──
function Kpi({ icon: Icon, label, value, accent }: { icon: typeof Building2; label: string; value: string; accent?: string }) {
  const colors = themeColors
  return (
    <Box bg={colors.bgCard} border="1px solid" borderColor={colors.border} borderRadius="xl" p={4} minW={0}>
      <Flex alignItems="center" gap={2} mb={1.5}>
        <Box display="flex" alignItems="center" justifyContent="center" w="26px" h="26px" borderRadius="lg" bg={`${accent ?? brand.green}14`} color={accent ?? brand.green} flexShrink={0}>
          <Icon size={14} />
        </Box>
        <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.1em" fontWeight="bold" color={colors.textMuted} truncate>{label}</Text>
      </Flex>
      <Text fontSize="lg" fontWeight="bold" color={colors.text} truncate>{value}</Text>
    </Box>
  )
}

// ════════════════ Sections ════════════════

function MiningRightsSection({ proveedor, colors }: { proveedor: ProveedorResponse; colors: typeof themeColors }) {
  const { t } = useT()
  const rights = proveedor.miningRights
  const [selectedId, setSelectedId] = useState<string | null>(rights?.[0]?.id ?? null)
  const [hoveredComponentId, setHoveredComponentId] = useState<string | null>(null)
  const selectedRight = rights?.find(r => r.id === selectedId) ?? null
  const geoJsonGeometry = useMemo(() => selectedRight ? parseGeoJson(selectedRight.geoJson) : null, [selectedRight])
  const geoJsonRings = useMemo(() => geometryRings(geoJsonGeometry), [geoJsonGeometry])
  // El contorno del DERECHO MINERO que publica INGEMMET. Es una capa aparte del poligono
  // de la CONCESION: el derecho es el edificio y la concesion del minero su departamento.
  const officialGeometry = useMemo(
    () => selectedRight ? parseGeoJson(selectedRight.officialGeoJson) : null, [selectedRight])
  const officialRings = useMemo(() => geometryRings(officialGeometry), [officialGeometry])
  const zone = useMemo(
    () => selectedRight ? (selectedRight.utmZone ?? getUtmZoneFromGeoJson(selectedRight.geoJson, selectedRight.ubigeoCode)) : 18,
    [selectedRight],
  )
  // Componentes convertidos de UTM (northing/easting) a lat/lng para graficarlos en el mapa.
  const componentMarkers = useMemo(() => {
    if (!selectedRight) return []
    return (selectedRight.components ?? [])
      .map(c => { const { lat, lng } = utmToLatLng(c.northing, c.easting, zone); return { comp: c, lat, lng } })
      .filter(m => isValidLatLng(m.lat, m.lng))
  }, [selectedRight, zone])
  const showMap = !!geoJsonGeometry || componentMarkers.length > 0
  // Encuadre: vértices del polígono + componentes (para que ambos sean visibles
  // aunque algún componente quede fuera del polígono).
  const boundsPoints = useMemo<LL[]>(
    () => {
      // Cuando la concesion no cae dentro del derecho minero, las dos formas pueden estar
      // a cientos de kilometros y encuadrar ambas muestra medio pais con los poligonos
      // invisibles. Manda la concesion, que es la geometria operativa.
      // Solo se descarta el derecho del encuadre si de verdad estan lejos (> 50 km entre
      // centros): una concesion que apenas asoma del derecho debe verse junto a el.
      const center = (pts: LL[]) => pts.length
        ? { lat: pts.reduce((a, p) => a + p.lat, 0) / pts.length, lng: pts.reduce((a, p) => a + p.lng, 0) / pts.length }
        : null
      const cc = center(geoJsonRings.flat())
      const co = center(officialRings.flat())
      const km = cc && co ? Math.hypot((cc.lat - co.lat) * 111, (cc.lng - co.lng) * 111 * Math.cos((cc.lat * Math.PI) / 180)) : 0
      const lejos = selectedRight?.concessionWithinOfficial === false && geoJsonRings.length > 0 && km > 50
      return [
        ...geoJsonRings.flat(),
        ...(lejos ? [] : officialRings.flat()),
        ...componentMarkers.map((m) => ({ lat: m.lat, lng: m.lng })),
      ]
    },
    [geoJsonRings, officialRings, componentMarkers, selectedRight],
  )

  return (
    <SectionCard icon={Mountain} title={t('proveedores.detail.derechosMinerosTitle', { count: rights?.length ?? 0 })} delay={0.05}>
      {(!rights || rights.length === 0) ? (
        <EmptyState label={t('proveedores.detail.noDerechos')} />
      ) : (
        <Flex gap={5} flexDirection={{ base: 'column', lg: 'row' }}>
          <VStack gap={2} alignItems="stretch" flex="1" minW="0">
            {rights.map(right => {
              const isSelected = right.id === selectedId
              const statusLabel = MINING_RIGHT_STATUSES.find(s => s.value === right.miningRightStatusId)?.label ?? right.miningRightStatus ?? t('proveedores.detail.desconocido')
              const isVigente = right.miningRightStatusId === 1
              const isSuspendido = right.miningRightStatusId === 4
              const isExtinto = right.miningRightStatusId === 2 // enum backend: 2=Extinto, 3=EnTramite
              const badgeBg = isVigente ? 'rgba(42, 138, 135, 0.15)' : isSuspendido ? 'rgba(234, 179, 8, 0.15)' : isExtinto ? 'rgba(200, 68, 47, 0.15)' : 'rgba(61, 111, 168, 0.15)'
              const badgeColor = isVigente ? '#1A7A4C' : isSuspendido ? '#a8740e' : isExtinto ? '#b23a26' : '#2b5285'
              const fb = formalizationBadge(right.formalizationStatus)
              return (
                <Flex
                  key={right.id}
                  p={3}
                  bg={isSelected ? `${brand.green}12` : colors.bgHover}
                  border="1px solid"
                  borderColor={isSelected ? brand.green : 'transparent'}
                  borderRadius="xl"
                  alignItems="center"
                  cursor="pointer"
                  transition="all 0.15s"
                  _hover={{ borderColor: brand.green, bg: `${brand.green}08` }}
                  onClick={() => setSelectedId(right.id)}
                >
                  <Box flex={1} minW="0">
                    <Flex gap={2} alignItems="center" mb={1} flexWrap="wrap">
                      <Text fontSize="sm" fontWeight="600" color={colors.text} truncate>{right.name}</Text>
                      <StatusPill label={statusLabel} bg={badgeBg} color={badgeColor} />
                      {fb && <StatusPill label={fb.label} bg={fb.bg} color={fb.color} />}
                    </Flex>
                    <Flex gap={3} flexWrap="wrap">
                      <Text fontSize="xs" color={colors.textMuted} fontFamily="monospace">{right.uniqueCode}</Text>
                      {right.ubigeoCode && (
                        <Flex gap={1} alignItems="center"><MapPin size={11} color={colors.textDimmed} /><Text fontSize="xs" color={colors.textMuted} truncate>{resolveUbigeo(right.ubigeoCode)}</Text></Flex>
                      )}
                    </Flex>
                  </Box>
                </Flex>
              )
            })}
          </VStack>

          {selectedRight && (
            <VStack flex="1.2" minW="0" gap={4} alignItems="stretch">
              {showMap ? (
                <Box minH={{ base: '280px', lg: '320px' }} height={{ base: '280px', lg: '320px' }} borderRadius="xl" overflow="hidden" border="1px solid" borderColor={colors.border}>
                  <KMap key={selectedRight.id} defaultCenter={{ lat: -14.0, lng: -70.5 }} defaultZoom={12} mapTypeId="hybrid">
                    <FitBounds points={boundsPoints} padding={30} maxZoom={16} nonce={boundsPoints.length} />
                    {officialRings.length > 0 && (
                      <GeoJsonPolygon rings={officialRings} color="#d49a2a" fillOpacity={0.05} />
                    )}
                    {geoJsonRings.length > 0 && (
                      <GeoJsonPolygon rings={geoJsonRings} color="#1d6664" fillOpacity={0.2} />
                    )}
                    {componentMarkers.map(({ comp, lat, lng }) => (
                      <AdvancedMarker
                        key={comp.id}
                        position={{ lat, lng }}
                        onMouseEnter={() => setHoveredComponentId(comp.id)}
                        onMouseLeave={() => setHoveredComponentId((cur) => (cur === comp.id ? null : cur))}
                      >
                        <ComponentPin
                          code={comp.componentTypeCode}
                          size={34}
                          color={comp.isOutsidePolygon ? '#b23a26' : undefined}
                        />
                      </AdvancedMarker>
                    ))}
                    {(() => {
                      const hovered = componentMarkers.find((m) => m.comp.id === hoveredComponentId)
                      if (!hovered) return null
                      const comp = hovered.comp
                      return (
                        <InfoWindow position={{ lat: hovered.lat, lng: hovered.lng }} pixelOffset={[0, -38]} disableAutoPan headerDisabled>
                          <Box fontSize="xs" fontFamily="'Instrument Sans', sans-serif" p={1}>
                            <Text fontWeight="700" color="#1a1a1a">{comp.componentTypeName ?? 'Componente'}</Text>
                            {comp.name && <Text color="#555">{comp.name}</Text>}
                            <Text fontFamily="monospace" color="#888">N {Math.round(comp.northing).toLocaleString('en')} · E {Math.round(comp.easting).toLocaleString('en')}</Text>
                            {comp.isOutsidePolygon && <Text color="#b23a26">Fuera del polígono</Text>}
                          </Box>
                        </InfoWindow>
                      )
                    })()}
                  </KMap>
                </Box>
              ) : (
                <Box p={6} textAlign="center" border="1px dashed" borderColor={colors.border} borderRadius="xl">
                  <Mountain size={28} color={colors.textDimmed} style={{ margin: '0 auto 8px' }} />
                  <Text tid="proveedores.detail.sinGeoref" fontSize="sm" color={colors.textMuted} />
                </Box>
              )}

              {/* Las dos formas del mapa son cosas distintas y sin leyenda no se
                  distinguen: el derecho minero es el contorno completo del catastro y la
                  concesion es el area que este minero trabaja adentro. */}
              {showMap && (
                <Flex gap={4} flexWrap="wrap" alignItems="center">
                  <Flex gap={1.5} alignItems="center">
                    <Box w="12px" h="12px" borderRadius="sm" border="2px solid" borderColor="#1d6664" bg="rgba(29,102,100,0.2)" />
                    <Text fontSize="11px" color={colors.textMuted} fontWeight="600">Concesión del minero</Text>
                  </Flex>
                  <Flex gap={1.5} alignItems="center">
                    <Box w="12px" h="12px" borderRadius="sm" border="2px solid #d49a2a" bg="rgba(212,154,42,0.06)" />
                    <Text fontSize="11px" color={colors.textMuted} fontWeight="600">
                      Derecho minero (INGEMMET)
                      {selectedRight.officialHectares != null && ` · ${selectedRight.officialHectares.toLocaleString('es-PE')} ha`}
                    </Text>
                  </Flex>
                  {officialRings.length === 0 && (
                    <Text fontSize="11px" color={colors.textDimmed}>Sin polígono del derecho minero.</Text>
                  )}
                  {/* Solo un aviso: una concesion fuera del derecho no bloquea nada. */}
                  {selectedRight.concessionWithinOfficial === false && (
                    <Text fontSize="11px" color="#b23a26" fontWeight="700">La concesión declarada no cae dentro del derecho minero.</Text>
                  )}
                </Flex>
              )}

              <Box bg={colors.bgHover} borderRadius="xl" p={4}>
                <Flex justifyContent="space-between" alignItems="center" mb={3}>
                  <Text tid="proveedores.detail.componentes" values={{ count: selectedRight.components?.length ?? 0 }} fontSize="10px" fontWeight="700" color={colors.textMuted} textTransform="uppercase" letterSpacing="0.05em" />
                  {componentMarkers.length > 0 && (
                    <Flex gap={3} alignItems="center">
                      <Flex gap={1} alignItems="center"><Box w="8px" h="8px" borderRadius="full" bg={brand.green} /><Text fontSize="10px" color={colors.textMuted}>En polígono</Text></Flex>
                      <Flex gap={1} alignItems="center"><Box w="8px" h="8px" borderRadius="full" bg="#b23a26" /><Text fontSize="10px" color={colors.textMuted}>Fuera</Text></Flex>
                    </Flex>
                  )}
                </Flex>
                {(!selectedRight.components || selectedRight.components.length === 0) ? (
                  <Text tid="proveedores.detail.sinComponentesRegistrados" fontSize="sm" color={colors.textMuted} />
                ) : (
                  <VStack gap={2} alignItems="stretch">
                    {selectedRight.components.map(comp => (
                      <Flex key={comp.id} p={3} bg="white" borderRadius="lg" gap={3} alignItems="center" border="1px solid" borderColor={colors.border}>
                        <Box flex={1}>
                          <Flex gap={2} alignItems="center">
                            <Text fontSize="sm" fontWeight="600" color={colors.text}>{comp.componentTypeName}</Text>
                            {/* Principal vs secundario cambia el peso del componente en la
                                fiscalizacion; sin la marca, la lista los muestra como iguales. */}
                            {comp.tierName && (
                              <StatusPill
                                label={comp.tierName}
                                bg={comp.tierCode === 'principal' ? 'rgba(10, 97, 54, 0.14)' : 'rgba(107, 114, 128, 0.15)'}
                                color={comp.tierCode === 'principal' ? brand.green : '#857b71'}
                              />
                            )}
                            {comp.isOutsidePolygon && <StatusPill label={t('proveedores.detail.fueraPoligono')} bg="rgba(200, 68, 47, 0.15)" color="#b23a26" />}
                          </Flex>
                          {comp.name && <Text fontSize="xs" color={colors.textMuted}>{comp.name}</Text>}
                          <Text fontSize="xs" fontFamily="monospace" color={colors.textMuted}>
                            N: {Math.round(comp.northing).toLocaleString('en')} | E: {Math.round(comp.easting).toLocaleString('en')}
                          </Text>
                        </Box>
                      </Flex>
                    ))}
                  </VStack>
                )}
              </Box>
            </VStack>
          )}
        </Flex>
      )}
    </SectionCard>
  )
}

function ContractsSection({ proveedor, colors }: { proveedor: ProveedorResponse; colors: typeof themeColors }) {
  const all = [proveedor.activeContract, ...(proveedor.contractHistory ?? [])].filter(Boolean) as NonNullable<ProveedorResponse['activeContract']>[]
  return (
    <SectionCard icon={FileSignature} title={`Contratos (${all.length})`} delay={0.05}>
      {all.length === 0 ? (
        <EmptyState label="Sin contratos registrados." />
      ) : (
        <VStack gap={3} alignItems="stretch">
          {all.map(ct => (
            <Flex key={ct.id} p={4} bg={colors.bgHover} borderRadius="xl" gap={3} alignItems="center" flexWrap="wrap">
              <Box flex={1} minW="200px">
                <Flex gap={2} alignItems="center" mb={1}>
                  <Text fontSize="sm" fontWeight="600" color={colors.text}>Contrato {ct.contractDate ? formatDate(ct.contractDate) : ''}</Text>
                  {ct.isActive
                    ? <StatusPill label="Activo" bg="rgba(42, 138, 135,0.15)" color="#1d6664" />
                    : <StatusPill label="Histórico" bg="rgba(107,114,128,0.15)" color="#857b71" />}
                </Flex>
                {ct.notes && <Text fontSize="xs" color={colors.textMuted}>{ct.notes}</Text>}
              </Box>
              {ct.documentFile && (
                <DocumentoDescarga variant="pill" storagePath={ct.documentFile.storagePath} caption="Contrato" fileName={ct.documentFile.originalFileName} />
              )}
            </Flex>
          ))}
        </VStack>
      )}
    </SectionCard>
  )
}

function ShareholdersSection({ proveedor, colors }: { proveedor: ProveedorResponse; colors: typeof themeColors }) {
  const shareholders = proveedor.shareholders ?? []
  const total = shareholders.reduce((s, x) => s + (x.participationPercent || 0), 0)
  const docTypeLabel = (t: number) => (t === 2 ? 'CE' : t === 3 ? 'Pasaporte' : 'DNI')
  return (
    <SectionCard
      icon={Users}
      title={`Accionistas y Beneficiarios Finales (${shareholders.length})`}
      action={shareholders.length > 0 ? <Text fontSize="xs" fontWeight="700" color={total > 100.001 ? '#b23a26' : brand.green}>Σ {total.toFixed(2)}%</Text> : undefined}
      delay={0.08}
    >
      {proveedor.requiresEnhancedDueDiligence && (
        <Flex gap={2} p={3} mb={3} borderRadius="lg" bg="rgba(212, 154, 42,0.10)" borderLeft="3px solid" borderColor="#a8740e" alignItems="flex-start">
          <TriangleAlert size={16} color="#a8740e" />
          <Text fontSize="xs" color="#8a5f0b">Debida Diligencia Intensificada: se detectaron antecedentes o inconsistencias en uno o más beneficiarios. Requiere revisión del Oficial de Cumplimiento.</Text>
        </Flex>
      )}
      {shareholders.length === 0 ? (
        <EmptyState label="Sin accionistas registrados." />
      ) : (
        <VStack gap={2} alignItems="stretch">
          {shareholders.map((sh: ShareholderResponse) => {
            const b = shareholderBadge(sh.validationStatus)
            return (
              <Flex key={sh.id} p={3} bg={colors.bgHover} borderRadius="xl" gap={3} alignItems="center" flexWrap="wrap">
                <Box flex={1} minW="200px">
                  <Flex gap={2} alignItems="center" mb={0.5} flexWrap="wrap">
                    <Text fontSize="sm" fontWeight="600" color={colors.text}>{sh.fullName}</Text>
                    <StatusPill label={sh.validationStatusName} bg={b.bg} color={b.color} icon={b.icon} />
                  </Flex>
                  <Flex gap={3} flexWrap="wrap">
                    <Text fontSize="xs" color={colors.textMuted} fontFamily="monospace">{docTypeLabel(sh.documentType)} {sh.documentNumber}</Text>
                    {sh.validationMessage && <Text fontSize="xs" color={colors.textMuted}>{sh.validationMessage}</Text>}
                  </Flex>
                </Box>
                <Text fontSize="sm" fontWeight="700" color={colors.text} fontFamily="monospace">{(sh.participationPercent ?? 0).toFixed(2)}%</Text>
                {sh.certificateFile && (
                  <DocumentoDescarga variant="pill" storagePath={sh.certificateFile.storagePath} caption="Certificado Único Laboral" fileName={sh.certificateFile.originalFileName} />
                )}
              </Flex>
            )
          })}
        </VStack>
      )}
    </SectionCard>
  )
}

function PowerValiditiesSection({ proveedor, colors }: { proveedor: ProveedorResponse; colors: typeof themeColors }) {
  const items = proveedor.powerValidities ?? []
  return (
    <SectionCard icon={KeyRound} title={`Vigencias de Poder (${items.length})`} delay={0.1}>
      {items.length === 0 ? (
        <EmptyState label="Sin vigencias de poder registradas." />
      ) : (
        <VStack gap={2} alignItems="stretch">
          {items.map(pv => (
            <Flex key={pv.id} p={3} bg={colors.bgHover} borderRadius="xl" gap={3} alignItems="center" flexWrap="wrap">
              <Box flex={1} minW="200px">
                <Flex gap={2} alignItems="center" mb={0.5}>
                  <Text fontSize="sm" fontWeight="600" color={colors.text} fontFamily="monospace">{pv.code}</Text>
                  {pv.isValid
                    ? <StatusPill label="Vigente" bg="rgba(42, 138, 135,0.15)" color="#1d6664" />
                    : <StatusPill label="Expirado" bg="rgba(200, 68, 47,0.15)" color="#b23a26" />}
                </Flex>
                <Text fontSize="xs" color={colors.textMuted}>{pv.personFullName ?? '-'} · Emitido {formatDate(pv.issueDate)} · Vence {formatDate(pv.expirationDate)}</Text>
              </Box>
              {pv.documentFile && (
                <DocumentoDescarga variant="pill" storagePath={pv.documentFile.storagePath} caption="Vigencia de Poder" fileName={pv.documentFile.originalFileName} />
              )}
            </Flex>
          ))}
        </VStack>
      )}
    </SectionCard>
  )
}

function ContactsSection({ proveedor, colors }: { proveedor: ProveedorResponse; colors: typeof themeColors }) {
  const { t } = useT()
  const persons = proveedor.persons ?? []
  return (
    <SectionCard icon={Users} title={t('proveedores.detail.contactosTitle', { count: persons.length })} delay={0.12}>
      {persons.length === 0 ? (
        <EmptyState label={t('proveedores.detail.noContactos')} />
      ) : (
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
          {persons.map(person => (
            <Flex key={person.id} p={4} bg={colors.bgHover} borderRadius="xl" alignItems="center" gap={3}>
              <Box width="36px" height="36px" borderRadius="full" bg={`${brand.green}20`} display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                <Text fontSize="xs" fontWeight="bold" color={brand.green}>
                  {person.fullName?.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() ?? '?'}
                </Text>
              </Box>
              <Box minW={0}>
                <Text fontSize="sm" fontWeight="600" color={colors.text} truncate>{person.fullName}</Text>
                <Flex gap={3} flexWrap="wrap">
                  {person.contactTypeName && <Text fontSize="xs" color={brand.green} fontWeight="600">{person.contactTypeName}</Text>}
                  {person.identityDoc && <Text fontSize="xs" color={colors.textMuted} fontFamily="monospace">{person.identityDoc}</Text>}
                  {person.email && <Text fontSize="xs" color={colors.textMuted} truncate>{person.email}</Text>}
                </Flex>
              </Box>
            </Flex>
          ))}
        </Grid>
      )}
    </SectionCard>
  )
}

function IgafomSection({ proveedor, colors }: { proveedor: ProveedorResponse; colors: typeof themeColors }) {
  const { t } = useT()
  const allIgafom = proveedor.miningRights?.flatMap(r => (r.igafomRecords ?? []).map(ig => ({ ...ig, miningRightName: r.name }))) ?? []
  return (
    <SectionCard icon={Shield} title={t('proveedores.detail.igafomTitle', { count: allIgafom.length })} delay={0.05}>
      {allIgafom.length === 0 ? (
        <EmptyState label={t('proveedores.detail.noIgafom')} />
      ) : (
        <VStack gap={4} alignItems="stretch">
          {allIgafom.map(ig => (
            <Box key={ig.id} p={4} bg={colors.bgHover} borderRadius="xl">
              <Text fontSize="xs" fontWeight="700" color={brand.green} mb={2}>{ig.miningRightName}</Text>
              <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                <InfoField label={t('proveedores.detail.nExpediente')} value={ig.fileNumber ?? '-'} />
                <InfoField label={t('proveedores.detail.fechaPresentacion')} value={formatDate(ig.presentationDate)} />
                <InfoField label={t('proveedores.detail.tipoIgafom')} value={ig.typeName ?? '-'} />
                <InfoField label={t('proveedores.detail.metodoExplotacion')} value={ig.exploitationMethodName ?? '-'} />
                <InfoField label={t('proveedores.detail.estadoDocumento')} value={ig.documentStatusName ?? '-'} />
              </Grid>
              <Flex gap={3} mt={3} flexWrap="wrap">
                {ig.receiptFile && <DocumentoDescarga variant="pill" storagePath={ig.receiptFile.storagePath} caption="Cargo de Presentación" fileName={ig.receiptFile.originalFileName} />}
                {ig.documentFile && <DocumentoDescarga variant="pill" storagePath={ig.documentFile.storagePath} caption="Documento IGAFOM/IGAC" fileName={ig.documentFile.originalFileName} />}
              </Flex>
            </Box>
          ))}
        </VStack>
      )}
    </SectionCard>
  )
}

function LegalDocumentsSection({ proveedor, colors }: { proveedor: ProveedorResponse; colors: typeof themeColors }) {
  const all = proveedor.miningRights?.flatMap(r => (r.legalDocumentRecords ?? []).map(ld => ({ ...ld, miningRightName: r.name }))) ?? []
  return (
    <SectionCard icon={Scale} title={`Otros Documentos Legales (${all.length})`} delay={0.08}>
      {all.length === 0 ? (
        <EmptyState label="Sin documentos legales complementarios." />
      ) : (
        <VStack gap={4} alignItems="stretch">
          {all.map(ld => (
            <Box key={ld.id} p={4} bg={colors.bgHover} borderRadius="xl">
              <Text fontSize="xs" fontWeight="700" color={brand.green} mb={2}>{ld.miningRightName}</Text>
              <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                <InfoField label="Tipo" value={ld.documentTypeName ?? '-'} />
                <InfoField label="N° Expediente" value={ld.fileNumber ?? '-'} />
                <InfoField label="Fecha presentación" value={formatDate(ld.presentationDate)} />
                <InfoField label="Estado" value={ld.documentStatusName ?? '-'} />
              </Grid>
              <Flex gap={3} mt={3} flexWrap="wrap">
                {ld.receiptFile && <DocumentoDescarga variant="pill" storagePath={ld.receiptFile.storagePath} caption="Cargo de Presentación" fileName={ld.receiptFile.originalFileName} />}
                {ld.documentFile && <DocumentoDescarga variant="pill" storagePath={ld.documentFile.storagePath} caption={ld.documentTypeName ?? 'Documento legal'} fileName={ld.documentFile.originalFileName} />}
              </Flex>
            </Box>
          ))}
        </VStack>
      )}
    </SectionCard>
  )
}

function ProductionSection({ proveedor, colors }: { proveedor: ProveedorResponse; colors: typeof themeColors }) {
  const { t } = useT()
  const all = proveedor.miningRights?.flatMap(r => (r.productionDeclarations ?? []).map(pd => ({ ...pd, miningRightName: r.name }))) ?? []
  return (
    <SectionCard icon={BarChart} title={t('proveedores.detail.produccionTitle', { count: all.length })} delay={0.1}>
      {all.length === 0 ? (
        <EmptyState label={t('proveedores.detail.noProduccion')} />
      ) : (
        <VStack gap={4} alignItems="stretch">
          {all.map(pd => (
            <Box key={pd.id} p={4} bg={colors.bgHover} borderRadius="xl">
              <Text fontSize="xs" fontWeight="700" color={brand.green} mb={2}>{pd.miningRightName}</Text>
              <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                <InfoField label={t('proveedores.detail.periodo')} value={enumLabel(PRODUCTION_PERIODS, pd.period)} />
                <InfoField label={t('proveedores.detail.anio')} value={pd.year?.toString() ?? '-'} />
                <InfoField label={t('proveedores.detail.nExpediente')} value={pd.fileNumber ?? '-'} />
                <InfoField label={t('proveedores.detail.fechaPresentacion')} value={formatDate(pd.presentationDate)} />
                <InfoField label={t('proveedores.detail.prodDiariaEstimada')} value={pd.dailyEstimatedProduction != null ? `${pd.dailyEstimatedProduction} TM` : '-'} />
                <InfoField label={t('proveedores.detail.totalMetalico')} value={pd.totalMetallic != null ? `${pd.totalMetallic} TM` : '-'} />
                <InfoField label={t('proveedores.detail.totalNoMetalico')} value={pd.totalNonMetallic != null ? `${pd.totalNonMetallic} TM` : '-'} />
              </Grid>
              {pd.documentFile && <Box mt={3}><DocumentoDescarga variant="pill" storagePath={pd.documentFile.storagePath} caption="Declaración DPS/DAC" fileName={pd.documentFile.originalFileName} /></Box>}
            </Box>
          ))}
        </VStack>
      )}
    </SectionCard>
  )
}

function RecpoSection({ proveedor, colors }: { proveedor: ProveedorResponse; colors: typeof themeColors }) {
  const { t } = useT()
  const records = proveedor.recpoRecords
  return (
    <SectionCard icon={ClipboardCheck} title={t('proveedores.detail.recpoTitle', { count: records?.length ?? 0 })} delay={0.12}>
      {(!records || records.length === 0) ? (
        <EmptyState label={t('proveedores.detail.noRecpo')} />
      ) : (
        <VStack gap={3} alignItems="stretch">
          {records.map((rec, i) => (
            <Box key={i} p={4} bg={colors.bgHover} borderRadius="xl">
              <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={4}>
                <InfoField label={t('proveedores.detail.codigoRecpo')} value={rec.recpoCode} />
                <InfoField label={t('proveedores.detail.fechaRecurso')} value={formatDate(rec.resourceDate)} />
                <InfoField label={t('proveedores.detail.condicion')} value={rec.condition ?? '-'} />
                <InfoField label={t('proveedores.detail.situacion')} value={etiquetaSituacionRecpo(rec.situation)} />
              </Grid>
            </Box>
          ))}
        </VStack>
      )}
    </SectionCard>
  )
}

function ExplosivesSection({ proveedor, colors }: { proveedor: ProveedorResponse; colors: typeof themeColors }) {
  const rights = (proveedor.miningRights ?? []).filter(r => (r.explosiveAuthorizations?.length ?? 0) > 0 || (r.explosiveHandlers?.length ?? 0) > 0 || r.usesExplosives)
  const handlerStatusPill = (status: string) => {
    if (status === 'valid') return <StatusPill label="Vigente" bg="rgba(42, 138, 135,0.15)" color="#1d6664" />
    if (status === 'expiringSoon') return <StatusPill label="Por vencer" bg="rgba(212, 154, 42,0.15)" color="#a8740e" />
    if (status === 'expired') return <StatusPill label="Vencida" bg="rgba(200, 68, 47,0.15)" color="#b23a26" />
    return <StatusPill label="Sin dato" bg="rgba(107,114,128,0.15)" color="#857b71" />
  }
  return (
    <SectionCard icon={Flask} title="IQBF / Explosivos" delay={0.14}>
      {rights.length === 0 ? (
        <EmptyState label="Ningún derecho minero declara uso de explosivos." />
      ) : (
        <VStack gap={4} alignItems="stretch">
          {rights.map(r => (
            <Box key={r.id} p={4} bg={colors.bgHover} borderRadius="xl">
              <Text fontSize="xs" fontWeight="700" color={brand.green} mb={3}>{r.name}</Text>

              {(r.explosiveAuthorizations?.length ?? 0) > 0 && (
                <VStack gap={2} alignItems="stretch" mb={3}>
                  {r.explosiveAuthorizations!.map(a => (
                    <Box key={a.id} p={3} bg="white" borderRadius="lg" border="1px solid" borderColor={colors.border}>
                      <Flex gap={2} alignItems="center" mb={1} flexWrap="wrap">
                        <Text fontSize="sm" fontWeight="600" color={colors.text}>{a.authorizationTypeName ?? 'Autorización'}</Text>
                        {a.resolutionNumber && <Text fontSize="xs" color={colors.textMuted} fontFamily="monospace">{a.resolutionNumber}</Text>}
                      </Flex>
                      <Flex gap={4} flexWrap="wrap">
                        <Text fontSize="xs" color={colors.textMuted}>Emisión: {formatDate(a.emissionDate)}</Text>
                        <Text fontSize="xs" color={colors.textMuted}>Vence: {formatDate(a.expirationDate)}</Text>
                        {a.authorizedExplosives && <Text fontSize="xs" color={colors.textMuted}>Explosivos: {a.authorizedExplosives}</Text>}
                        {a.maxCapacity && <Text fontSize="xs" color={colors.textMuted}>Cap.: {a.maxCapacity}</Text>}
                      </Flex>
                      {a.documentFile && <Box mt={2}><DocumentoDescarga variant="pill" storagePath={a.documentFile.storagePath} caption="Autorización de explosivos" fileName={a.documentFile.originalFileName} /></Box>}
                    </Box>
                  ))}
                </VStack>
              )}

              {(r.explosiveHandlers?.length ?? 0) > 0 && (
                <VStack gap={2} alignItems="stretch">
                  <Text fontSize="10px" fontWeight="700" color={colors.textMuted} textTransform="uppercase" letterSpacing="0.05em">Manipuladores</Text>
                  {r.explosiveHandlers!.map(h => (
                    <Flex key={h.id} p={3} bg="white" borderRadius="lg" border="1px solid" borderColor={colors.border} gap={3} alignItems="center" flexWrap="wrap">
                      <Box flex={1} minW="180px">
                        <Flex gap={2} alignItems="center" flexWrap="wrap">
                          <Text fontSize="sm" fontWeight="600" color={colors.text}>{h.fullName}</Text>
                          {handlerStatusPill(h.status)}
                          {h.sucamecValidated && <StatusPill label="SUCAMEC" bg={`${brand.green}18`} color={brand.green} />}
                        </Flex>
                        <Text fontSize="xs" color={colors.textMuted} fontFamily="monospace">{h.identityDoc ?? '-'} · Lic. {h.licenseNumber ?? '-'} · Vence {formatDate(h.expirationDate)}</Text>
                      </Box>
                    </Flex>
                  ))}
                </VStack>
              )}
            </Box>
          ))}
        </VStack>
      )}
    </SectionCard>
  )
}

function FinanceSection({ proveedor, colors }: { proveedor: ProveedorResponse; colors: typeof themeColors }) {
  const { t } = useT()
  return (
    <SectionCard icon={Landmark} title={t('proveedores.detail.finanzasTitle')} delay={0.05}>
      <VStack gap={5} alignItems="stretch">
        <Box>
          <Text tid="proveedores.detail.cuentasBancarias" values={{ count: proveedor.bankAccounts?.length ?? 0 }} fontSize="10px" fontWeight="700" color={colors.textMuted} textTransform="uppercase" letterSpacing="0.05em" mb={3} />
          {(!proveedor.bankAccounts || proveedor.bankAccounts.length === 0) ? (
            <Text tid="proveedores.detail.noCuentas" fontSize="sm" color={colors.textMuted} />
          ) : (
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={2}>
              {proveedor.bankAccounts.map(acc => (
                <Flex key={acc.id} p={3} bg={colors.bgHover} borderRadius="xl" alignItems="center" gap={3}>
                  <Box flex={1} minW={0}>
                    <Flex gap={2} alignItems="center" mb={1}>
                      <Text fontSize="sm" fontWeight="600" color={colors.text} fontFamily="monospace" truncate>{acc.accountNumber}</Text>
                      <StatusPill label={acc.accountTypeName} bg={`${brand.green}20`} color={brand.green} />
                    </Flex>
                    <Text fontSize="xs" color={colors.textMuted}>{acc.bankEntityName}</Text>
                  </Box>
                </Flex>
              ))}
            </Grid>
          )}
        </Box>

        <Box>
          <Text fontSize="10px" fontWeight="700" color={colors.textMuted} textTransform="uppercase" letterSpacing="0.05em" mb={3}>Reportes Tributarios ({proveedor.taxReports?.length ?? 0})</Text>
          {(!proveedor.taxReports || proveedor.taxReports.length === 0) ? (
            <Text fontSize="sm" color={colors.textMuted}>Sin reportes tributarios.</Text>
          ) : (
            <VStack gap={2} alignItems="stretch">
              {proveedor.taxReports.map(tr => (
                <Flex key={tr.id} p={3} bg={colors.bgHover} borderRadius="xl" gap={3} alignItems="center" flexWrap="wrap">
                  <Box flex={1} minW="180px">
                    <Flex gap={2} alignItems="center" mb={0.5}>
                      <Text fontSize="sm" fontWeight="600" color={colors.text}>{tr.code ?? 'Reporte'}</Text>
                      {tr.isValid
                        ? <StatusPill label="Vigente" bg="rgba(42, 138, 135,0.15)" color="#1d6664" />
                        : <StatusPill label="Expirado" bg="rgba(200, 68, 47,0.15)" color="#b23a26" />}
                    </Flex>
                    <Text fontSize="xs" color={colors.textMuted}>Emitido {formatDate(tr.issueDate)} · Vence {formatDate(tr.expirationDate)}</Text>
                  </Box>
                  {tr.documentFile && <DocumentoDescarga variant="pill" storagePath={tr.documentFile.storagePath} caption="Reporte tributario" fileName={tr.documentFile.originalFileName} />}
                </Flex>
              ))}
            </VStack>
          )}
        </Box>

        <Box>
          <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={5}>
            <InfoField label={t('proveedores.detail.splaftDeclarado')} value={proveedor.splaftDeclared ? t('proveedores.detail.yes') : t('proveedores.detail.no')} />
          </Grid>
          {proveedor.splaftDocumentFile && (
            <Box mt={3}><DocumentoDescarga variant="pill" storagePath={proveedor.splaftDocumentFile.storagePath} caption="Declaración SPLAFT firmada" fileName={proveedor.splaftDocumentFile.originalFileName} /></Box>
          )}
        </Box>
      </VStack>
    </SectionCard>
  )
}

// ════════════════ Page ════════════════

type TabKey = 'resumen' | 'identidad' | 'societario' | 'derechos' | 'cumplimiento' | 'finanzas' | 'certificacion' | 'documentos'

export default function DetalleProveedorPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const colors = themeColors
  const { t } = useT()

  const [proveedor, setProveedor] = useState<ProveedorResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [approvals, setApprovals] = useState<SupplierApprovalListItemResponse[]>([])
  const [statusHistory, setStatusHistory] = useState<RegistrationStatusChangeResponse[]>([])
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState<TabKey>('resumen')

  const handleSubmitForEvaluation = async () => {
    if (!id) return
    setSubmitting(true)
    try {
      await proveedoresService.submitForEvaluation(id)
      toaster.success({ description: t('proveedores.detail.submitForEvaluationSuccess') })
      const data = await proveedoresService.getById(id)
      setProveedor(data)
      setShowSubmitConfirm(false)
    } catch {
      // apiClient already surfaces the error toast
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (!id) return
    const load = async () => {
      setLoading(true)
      try {
        const data = await proveedoresService.getById(id)
        setProveedor(data)
      } catch { /* silently fail */ } finally {
        setLoading(false)
      }
    }
    load()
  }, [id])

  useEffect(() => {
    if (!id) return
    const loadApprovals = async () => {
      try {
        const data = await proveedoresService.getApprovals(id)
        setApprovals(data)
      } catch { /* */ }
    }
    loadApprovals()
  }, [id])

  useEffect(() => {
    if (!id) return
    const loadStatusHistory = async () => {
      try {
        setStatusHistory(await proveedoresService.getStatusHistory(id))
      } catch { /* el historial es informativo: si falla, la ficha se muestra igual */ }
    }
    loadStatusHistory()
  }, [id])

  if (loading) {
    return (
      <VStack gap={4} py={20}>
        <Text tid="proveedores.detail.loadingProveedor" color={colors.textMuted} fontSize="sm" />
      </VStack>
    )
  }

  if (!proveedor) {
    return (
      <VStack gap={4} py={20}>
        <Text tid="proveedores.detail.notFound" color={colors.text} fontSize="lg" />
        <Box as="button" type="button" color={brand.green} fontSize="sm" cursor="pointer" _hover={{ textDecoration: 'underline' }} onClick={() => navigate('/app/proveedores')}>
          {t('proveedores.detail.backButton')}
        </Box>
      </VStack>
    )
  }

  const ms = proveedor.miningSupplier
  const regStatus = registrationStatusBadge(proveedor.registrationStatus)
  const rightsCount = proveedor.miningRights?.length ?? 0
  const shareholdersCount = proveedor.shareholders?.length ?? 0
  const docsCount = proveedor.documents?.length ?? 0
  const categoryLabel = ms ? enumLabel(MINING_CATEGORIES, ms.categoryId) : '-'

  // La capacidad productiva se declara por IGAFOM, o sea por derecho minero. El KPI leia
  // `proveedor.productiveCapacity`, que vive en MiningSupplier y no lo escribe nadie: el
  // dato salia siempre en "-" aunque el usuario lo hubiera cargado. Se arma desde donde
  // el dato realmente esta, y cuando los D.M. declaran capacidades distintas se dice
  // cuantas hay en vez de elegir una al azar.
  const capacidadesDeclaradas = Array.from(new Set(
    (proveedor.miningRights ?? [])
      .flatMap(r => r.igafomRecords ?? [])
      .map(ig => ig.productiveCapacity?.trim())
      .filter((c): c is string => !!c)
  ))
  const capacidadProductiva =
    capacidadesDeclaradas.length === 0 ? '-'
    : capacidadesDeclaradas.length === 1 ? capacidadesDeclaradas[0]
    : `${capacidadesDeclaradas.length} declaradas`

  const TABS: { key: TabKey; label: string; icon: typeof Building2; show?: boolean }[] = [
    { key: 'resumen', label: 'Resumen', icon: LayoutDashboard },
    { key: 'identidad', label: 'Identidad', icon: Building2 },
    { key: 'societario', label: 'Societario', icon: Users },
    { key: 'derechos', label: 'Derechos Mineros', icon: Mountain },
    { key: 'cumplimiento', label: 'Cumplimiento', icon: Shield },
    { key: 'finanzas', label: 'Finanzas', icon: Landmark },
    { key: 'certificacion', label: 'Certificación', icon: Award },
    { key: 'documentos', label: 'Documentos', icon: FileText },
  ]

  return (
    <VStack gap={5} alignItems="stretch">
      {/* Breadcrumb + Back */}
      <Flex alignItems="center" gap={3}>
        <Box as="button" type="button" p={2} borderRadius="xl" cursor="pointer" color={colors.textMuted} _hover={{ bg: colors.bgHover, color: colors.text }} transition="all 0.2s" onClick={() => navigate('/app/proveedores')}>
          <ArrowLeft size={20} />
        </Box>
        <Flex alignItems="center" gap={2} minW={0}>
          <Text fontSize="sm" color={colors.textMuted} cursor="pointer" _hover={{ color: brand.green }} onClick={() => navigate('/app/proveedores')}>
            {t('proveedores.detail.breadcrumbMineros')}
          </Text>
          <Text fontSize="sm" color={colors.textDimmed}>/</Text>
          <Text fontSize="sm" color={colors.text} fontWeight="600" truncate>{proveedor.legalName}</Text>
        </Flex>
      </Flex>

      {/* Hero header */}
      <MotionBox
        bg={colors.bgCard}
        border="1px solid"
        borderColor={colors.border}
        borderRadius="2xl"
        overflow="hidden"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <Box bg={`linear-gradient(135deg, ${brand.green}, #0a3d29)`} px={6} py={6}>
          <Flex justifyContent="space-between" alignItems="flex-start" gap={4} flexWrap="wrap">
            <Flex gap={4} alignItems="center" minW={0}>
              <Box w="56px" h="56px" borderRadius="2xl" bg="rgba(255,255,255,0.16)" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                <Building2 size={26} color="white" />
              </Box>
              <Box minW={0}>
                <Text fontSize="xl" fontWeight="bold" color="white" truncate>{proveedor.legalName}</Text>
                {proveedor.commercialName && <Text fontSize="sm" color="rgba(255,255,255,0.8)" truncate>{proveedor.commercialName}</Text>}
                <Text fontSize="xs" color="rgba(255,255,255,0.75)" fontFamily="monospace" mt={0.5}>RUC {proveedor.ruc}</Text>
              </Box>
            </Flex>
            <Flex alignItems="center" gap={3} flexWrap="wrap">
              <Box px={3} py={1} borderRadius="md" fontSize="10px" fontWeight="bold" textTransform="uppercase" bg="rgba(255,255,255,0.18)" color="white">
                {regStatus.label}
              </Box>
              {proveedor.registrationStatus === 0 && proveedor.wizardCompletedAt != null && (
                <Box as="button" type="button" display="inline-flex" alignItems="center" gap={2} px={4} py={2} borderRadius="md" bg="white" color={brand.green} fontSize="sm" fontWeight="700" cursor="pointer" _hover={{ opacity: 0.9 }} onClick={() => setShowSubmitConfirm(true)}>
                  <Send size={14} />
                  {t('proveedores.detail.submitForEvaluation')}
                </Box>
              )}
            </Flex>
          </Flex>
        </Box>

        {/* KPI strip */}
        <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' }} gap={3} p={4}>
          <Kpi icon={Database} label="Categoría minera" value={categoryLabel} />
          <Kpi icon={Mountain} label="Derechos mineros" value={String(rightsCount)} />
          {proveedor.isCorporation && <Kpi icon={Users} label="Accionistas" value={String(shareholdersCount)} accent={proveedor.requiresEnhancedDueDiligence ? '#a8740e' : undefined} />}
          <Kpi icon={BarChart} label="Capacidad productiva" value={capacidadProductiva} />
          <Kpi icon={Award} label="Certificado" value={proveedor.cuid?.cuidCode ?? 'Aún no autorizado'} accent={proveedor.cuid ? '#1d6664' : '#857b71'} />
        </Grid>
      </MotionBox>

      {/* Submit for evaluation confirm modal */}
      {showSubmitConfirm && (
        <Box position="fixed" top={0} left={0} right={0} bottom={0} bg="rgba(0,0,0,0.5)" zIndex={50} display="flex" alignItems="center" justifyContent="center" onClick={() => { if (!submitting) setShowSubmitConfirm(false) }}>
          <Box bg={colors.bgCard} border="1px solid" borderColor={colors.border} borderRadius="2xl" p={6} maxW="480px" w="90%" onClick={(e) => e.stopPropagation()}>
            <Text fontSize="lg" fontWeight="bold" color={colors.text} mb={2}>{t('proveedores.detail.submitForEvaluationConfirmTitle')}</Text>
            <Text fontSize="sm" color={colors.textMuted} mb={5}>{t('proveedores.detail.submitForEvaluationConfirmBody')}</Text>
            <Flex gap={3} justifyContent="flex-end">
              <Box as="button" type="button" px={4} py={2} borderRadius="md" border="1px solid" borderColor={colors.border} color={colors.text} fontSize="sm" fontWeight="600" cursor={submitting ? 'not-allowed' : 'pointer'} opacity={submitting ? 0.5 : 1} _hover={submitting ? {} : { bg: colors.bgHover }} onClick={() => setShowSubmitConfirm(false)} disabled={submitting}>
                {t('proveedores.detail.submitForEvaluationCancel')}
              </Box>
              <Box as="button" type="button" px={4} py={2} borderRadius="md" bg={submitting ? '#b5aa9d' : brand.green} color="white" fontSize="sm" fontWeight="700" cursor={submitting ? 'not-allowed' : 'pointer'} _hover={submitting ? {} : { opacity: 0.9 }} onClick={handleSubmitForEvaluation} disabled={submitting}>
                {t('proveedores.detail.submitForEvaluationConfirm')}
              </Box>
            </Flex>
          </Box>
        </Box>
      )}

      {/* Sticky tab nav */}
      <Box position="sticky" top={0} zIndex={20} bg={colors.bg} py={2} mx={-1} px={1}>
        <Flex gap={1} overflowX="auto" bg={colors.bgCard} border="1px solid" borderColor={colors.border} borderRadius="xl" p={1.5}>
          {TABS.map(tab => {
            const isActive = activeTab === tab.key
            const Icon = tab.icon
            return (
              <Flex
                key={tab.key}
                as="button"
                type="button"
                alignItems="center"
                gap={2}
                px={4}
                py={2}
                borderRadius="lg"
                fontSize="sm"
                fontWeight="600"
                whiteSpace="nowrap"
                cursor="pointer"
                transition="all 0.15s"
                bg={isActive ? brand.green : 'transparent'}
                color={isActive ? 'white' : colors.textMuted}
                _hover={isActive ? {} : { bg: colors.bgHover, color: colors.text }}
                onClick={() => setActiveTab(tab.key)}
              >
                <Icon size={15} />
                {tab.label}
              </Flex>
            )
          })}
        </Flex>
      </Box>

      {/* ── Tab content ── */}
      {activeTab === 'resumen' && (
        <VStack gap={5} alignItems="stretch">
          {proveedor.cuid && <CertificateCard proveedor={proveedor} colors={colors} navigate={navigate} />}
          <SectionCard icon={Building2} title={t('proveedores.detail.identidadSunat')} delay={0.05}>
            <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={5}>
              <InfoField label={t('proveedores.detail.ruc')} value={proveedor.ruc} />
              <InfoField label={t('proveedores.detail.razonSocial')} value={proveedor.legalName} />
              <InfoField label={t('proveedores.detail.tipoContribuyente')} value={proveedor.taxpayerType ?? '-'} />
              <InfoField label={t('proveedores.detail.estadoContribuyente')} value={proveedor.taxpayerStatus ?? '-'} />
              <InfoField label={t('proveedores.detail.condicionContribuyente')} value={proveedor.taxpayerCondition ?? '-'} />
              <InfoField label={t('proveedores.detail.categoriaMinera')} value={categoryLabel} />
            </Grid>
          </SectionCard>
          <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={5}>
            <ComplianceSummaryCard proveedor={proveedor} colors={colors} />
            <Box>
              {proveedor.isCorporation
                ? <ShareholdersSection proveedor={proveedor} colors={colors} />
                : <ContactsSection proveedor={proveedor} colors={colors} />}
            </Box>
          </Grid>
        </VStack>
      )}

      {activeTab === 'identidad' && (
        <VStack gap={5} alignItems="stretch">
          <SectionCard icon={Building2} title={t('proveedores.detail.identidadSunat')} delay={0.05}>
            <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={5}>
              <InfoField label={t('proveedores.detail.ruc')} value={proveedor.ruc} />
              <InfoField label={t('proveedores.detail.razonSocial')} value={proveedor.legalName} />
              <InfoField label={t('proveedores.detail.nombreComercial')} value={proveedor.commercialName ?? '-'} />
              {proveedor.economicActivities && proveedor.economicActivities.length > 0 ? (
                <Box gridColumn={{ sm: 'span 2', lg: 'span 3' }}>
                  <Text tid="proveedores.detail.actividadEconomica" fontSize="10px" textTransform="uppercase" letterSpacing="0.12em" fontWeight="bold" color={colors.textMuted} />
                  <VStack gap={1} alignItems="stretch" mt={1}>
                    {proveedor.economicActivities.map((act, i) => <Text key={i} fontSize="sm" fontWeight="500" color={colors.text}>{act}</Text>)}
                  </VStack>
                </Box>
              ) : (
                <InfoField label={t('proveedores.detail.actividadEconomica')} value="-" />
              )}
              <Box gridColumn={{ sm: 'span 2' }}><InfoField label={t('proveedores.detail.direccionFiscal')} value={proveedor.taxAddress ?? '-'} /></Box>
              <InfoField label={t('proveedores.detail.ubigeo')} value={resolveUbigeo(proveedor.ubigeoCode)} />
              <InfoField label={t('proveedores.detail.telefono')} value={proveedor.mainPhone ?? '-'} />
              <InfoField label={t('proveedores.detail.email')} value={proveedor.mainEmail ?? '-'} />
              <InfoField label={t('proveedores.detail.sitioWeb')} value={proveedor.website ?? '-'} />
              <InfoField label={t('proveedores.detail.estadoContribuyente')} value={proveedor.taxpayerStatus ?? '-'} />
              <InfoField label={t('proveedores.detail.condicionContribuyente')} value={proveedor.taxpayerCondition ?? '-'} />
              <InfoField label={t('proveedores.detail.tipoContribuyente')} value={proveedor.taxpayerType ?? '-'} />
            </Grid>
          </SectionCard>

          <SectionCard icon={Database} title={t('proveedores.detail.acreditacionMinem')} delay={0.08}>
            <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={5}>
              <InfoField label={t('proveedores.detail.categoriaMinera')} value={categoryLabel} />
              <InfoField label={t('proveedores.detail.capacidadProductiva')} value={capacidadProductiva} />
              <InfoField label={t('proveedores.detail.inicioActividades')} value={formatDate(proveedor.activityStartDate)} />
              <InfoField label={t('proveedores.detail.categoriaIRLabel')} value={ms ? enumLabel(INCOME_TAX_CATEGORIES, ms.incomeTaxCategoryId) : '-'} />
            </Grid>
          </SectionCard>

          <ContactsSection proveedor={proveedor} colors={colors} />
        </VStack>
      )}

      {activeTab === 'societario' && (
        <VStack gap={5} alignItems="stretch">
          <ContractsSection proveedor={proveedor} colors={colors} />
          {proveedor.isCorporation && <ShareholdersSection proveedor={proveedor} colors={colors} />}
          {/* Misma regla que el wizard: la vigencia de poder solo se registra para
              personas juridicas (RUC 20). El detalle la mostraba siempre, asi que en un
              minero persona natural aparecia la etiqueta de una seccion que nunca se
              puede llenar — el cliente lo reporto como "veo la etiqueta pero no recuerdo
              haber registrado ese dato". */}
          {(proveedor.ruc ?? '').startsWith('20') && (
            <PowerValiditiesSection proveedor={proveedor} colors={colors} />
          )}
          <ContactsSection proveedor={proveedor} colors={colors} />
        </VStack>
      )}

      {activeTab === 'derechos' && (
        <VStack gap={5} alignItems="stretch">
          <MiningRightsSection proveedor={proveedor} colors={colors} />
        </VStack>
      )}

      {activeTab === 'cumplimiento' && (
        <VStack gap={5} alignItems="stretch">
          <IgafomSection proveedor={proveedor} colors={colors} />
          <LegalDocumentsSection proveedor={proveedor} colors={colors} />
          <ProductionSection proveedor={proveedor} colors={colors} />
          <ExplosivesSection proveedor={proveedor} colors={colors} />
          <RecpoSection proveedor={proveedor} colors={colors} />
        </VStack>
      )}

      {activeTab === 'finanzas' && (
        <VStack gap={5} alignItems="stretch">
          <FinanceSection proveedor={proveedor} colors={colors} />
        </VStack>
      )}

      {activeTab === 'certificacion' && (
        <VStack gap={5} alignItems="stretch">
          {proveedor.cuid ? <CertificateCard proveedor={proveedor} colors={colors} navigate={navigate} /> : (
            <SectionCard icon={Award} title="Certificado CUID" delay={0.05}>
              <EmptyState label="El certificado se emite al autorizar al minero. Este todavía no fue autorizado." />
            </SectionCard>
          )}
          {approvals.length > 0 && <ApprovalsTable approvals={approvals} colors={colors} />}
          <StatusHistoryTable cambios={statusHistory} colors={colors} />
        </VStack>
      )}

      {activeTab === 'documentos' && (
        <VStack gap={5} alignItems="stretch">
          <SectionCard icon={FileText} title={t('proveedores.detail.documentosTitle', { count: docsCount })} delay={0.05}>
            {(!proveedor.documents || proveedor.documents.length === 0) ? (
              <EmptyState label={t('proveedores.detail.sinDocumentos')} />
            ) : (
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
                {proveedor.documents.map(doc => (
                  <Flex key={doc.id} p={4} bg={colors.bgHover} borderRadius="xl" alignItems="center" gap={3}>
                    <Box display="flex" alignItems="center" justifyContent="center" w="36px" h="36px" borderRadius="lg" bg={`${brand.green}20`} color={brand.green} flexShrink={0}><FileText size={18} /></Box>
                    <Box minW={0}>
                      <Text fontSize="sm" fontWeight="600" color={colors.text} truncate>{doc.documentTypeName ?? t('proveedores.detail.documentos')}</Text>
                      {doc.fileName && <Text fontSize="xs" color={colors.textMuted} truncate>{doc.fileName}</Text>}
                    </Box>
                  </Flex>
                ))}
              </Grid>
            )}
          </SectionCard>
        </VStack>
      )}
    </VStack>
  )
}

// ── Certificate card (reused in Resumen + Certificación) ──
function CertificateCard({ proveedor, colors, navigate }: { proveedor: ProveedorResponse; colors: typeof themeColors; navigate: ReturnType<typeof useNavigate> }) {
  const { t } = useT()
  if (!proveedor.cuid) return null
  return (
    <MotionBox bg={colors.bgCard} border="1px solid" borderColor="rgba(42, 138, 135, 0.3)" borderRadius="2xl" p={6} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
      <Flex alignItems="center" gap={2} mb={3}>
        <Award size={18} color={brand.green} />
        <Text tid="proveedores.detail.certificateTitle" fontSize="md" fontWeight="bold" color={colors.text} />
      </Flex>
      <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={4} mb={4}>
        <InfoField label={t('proveedores.detail.codigoCuid')} value={proveedor.cuid.cuidCode} />
        <InfoField label="Versión" value={`v${proveedor.cuid.version}`} />
        <InfoField label={t('proveedores.detail.fechaAutorizacion')} value={formatDateTime(proveedor.cuid.authorizedAt)} />
        <InfoField label="Vence" value={formatDateTime(proveedor.cuid.expiresAt)} />
      </Grid>
      <Flex gap={3} flexWrap="wrap">
        {proveedor.cuid.certificateUrl && (
          <DocumentoDescarga variant="pill" caption={t('proveedores.detail.downloadCertificate')} descargar={() => proveedoresService.downloadCertificate(proveedor.cuid!.certificateUrl!)} />
        )}
        {proveedor.cuid.expedienteCode && (
          <Box as="button" type="button" display="inline-flex" alignItems="center" gap={2} px={4} py={2} bg={brand.green} color="white" borderRadius="md" fontSize="sm" fontWeight="700" cursor="pointer" _hover={{ opacity: 0.9 }} onClick={() => navigate(`/app/identidad/expedientes/${encodeURIComponent(proveedor.cuid!.expedienteCode!)}`)}>
            <LuFolderOpen size={14} />
            Ver expediente digital
          </Box>
        )}
      </Flex>
    </MotionBox>
  )
}

// ── Compliance summary (Resumen tab) ──
function ComplianceSummaryCard({ proveedor, colors }: { proveedor: ProveedorResponse; colors: typeof themeColors }) {
  const rights = proveedor.miningRights ?? []
  const counts = [
    { label: 'IGAFOM/IGAC', value: rights.reduce((s, r) => s + (r.igafomRecords?.length ?? 0), 0), icon: Shield },
    { label: 'Documentos legales', value: rights.reduce((s, r) => s + (r.legalDocumentRecords?.length ?? 0), 0), icon: Scale },
    { label: 'Declaraciones producción', value: rights.reduce((s, r) => s + (r.productionDeclarations?.length ?? 0), 0), icon: BarChart },
    { label: 'Autorizaciones explosivos', value: rights.reduce((s, r) => s + (r.explosiveAuthorizations?.length ?? 0), 0), icon: Flask },
    { label: 'Registros RECPO', value: proveedor.recpoRecords?.length ?? 0, icon: ClipboardCheck },
    { label: 'Reportes tributarios', value: proveedor.taxReports?.length ?? 0, icon: Receipt },
  ]
  return (
    <SectionCard icon={ClipboardCheck} title="Resumen de cumplimiento" delay={0.08}>
      <Grid templateColumns="repeat(2, 1fr)" gap={3}>
        {counts.map(c => {
          const Icon = c.icon
          return (
            <Flex key={c.label} p={3} bg={colors.bgHover} borderRadius="xl" alignItems="center" gap={3}>
              <Box display="flex" alignItems="center" justifyContent="center" w="34px" h="34px" borderRadius="lg" bg={`${brand.green}14`} color={brand.green} flexShrink={0}><Icon size={16} /></Box>
              <Box minW={0}>
                <Text fontSize="lg" fontWeight="bold" color={colors.text}>{c.value}</Text>
                <Text fontSize="11px" color={colors.textMuted} truncate>{c.label}</Text>
              </Box>
            </Flex>
          )
        })}
      </Grid>
    </SectionCard>
  )
}

// ── Approval history table (Certificación tab) ──
function StatusHistoryTable({ cambios, colors }: { cambios: RegistrationStatusChangeResponse[]; colors: typeof themeColors }) {
  return (
    <SectionCard icon={Shield} title={`Historial de estados (${cambios.length})`} delay={0.1}>
      {cambios.length === 0 ? (
        // Los mineros registrados antes de que existiera esta tabla no tienen historial:
        // no se puede reconstruir hacia atras lo que nunca se guardo.
        <EmptyState label="Sin cambios de estado registrados. El historial se construye desde el primer cambio posterior a la puesta en marcha de esta función." />
      ) : (
        <Box overflowX="auto">
          <Box as="table" w="100%" fontSize="sm">
            <Box as="thead">
              <Box as="tr" color={colors.textMuted} textAlign="left" fontSize="xs" textTransform="uppercase" letterSpacing="0.08em">
                <Box as="th" py={2} pr={3}>Cambio</Box>
                <Box as="th" py={2} pr={3}>Motivo</Box>
                <Box as="th" py={2} pr={3}>Usuario</Box>
                <Box as="th" py={2} pr={3}>Fecha y hora</Box>
                <Box as="th" py={2}>Origen</Box>
              </Box>
            </Box>
            <Box as="tbody">
              {cambios.map(c => {
                const desde = c.fromStatus != null ? registrationStatusBadge(c.fromStatus) : null
                const hasta = registrationStatusBadge(c.toStatus)
                return (
                  <Box as="tr" key={c.id} borderTop="1px solid" borderColor={colors.border}>
                    <Box as="td" py={2.5} pr={3}>
                      <Flex alignItems="center" gap={1.5} flexWrap="wrap">
                        {desde && (
                          <>
                            <StatusPill label={desde.label} bg={desde.bg} color={desde.color} />
                            <Text as="span" color={colors.textMuted}>→</Text>
                          </>
                        )}
                        <StatusPill label={hasta.label} bg={hasta.bg} color={hasta.color} />
                      </Flex>
                    </Box>
                    <Box as="td" py={2.5} pr={3} color={colors.text}>{c.reason ?? '—'}</Box>
                    <Box as="td" py={2.5} pr={3} color={colors.text}>{c.changedByUsername ?? '—'}</Box>
                    <Box as="td" py={2.5} pr={3} color={colors.text} whiteSpace="nowrap">{formatDateTime(c.changedAt)}</Box>
                    <Box as="td" py={2.5} color={colors.textMuted} fontFamily="monospace" fontSize="xs">{c.ipAddress ?? '—'}</Box>
                  </Box>
                )
              })}
            </Box>
          </Box>
        </Box>
      )}
    </SectionCard>
  )
}

function ApprovalsTable({ approvals, colors }: { approvals: SupplierApprovalListItemResponse[]; colors: typeof themeColors }) {
  return (
    <SectionCard icon={Shield} title="Historial de aprobaciones" delay={0.08}>
      <Box overflowX="auto">
        <Box as="table" w="100%" fontSize="sm">
          <Box as="thead">
            <Box as="tr" color={colors.textMuted} textAlign="left" fontSize="xs" textTransform="uppercase" letterSpacing="0.08em">
              <Box as="th" py={2} pr={3}>V</Box>
              <Box as="th" py={2} pr={3}>CUID</Box>
              <Box as="th" py={2} pr={3}>Tipo</Box>
              <Box as="th" py={2} pr={3}>Fecha</Box>
              <Box as="th" py={2} pr={3}>Vence</Box>
              <Box as="th" py={2} pr={3}>Estado</Box>
              <Box as="th" py={2} pr={3}>Autorizador</Box>
              <Box as="th" py={2}>Acciones</Box>
            </Box>
          </Box>
          <Box as="tbody">
            {approvals.map(a => {
              const triggerLabel = a.trigger === 0 ? 'Inicial' : a.trigger === 1 ? 'Re-aprobación' : 'Renovación'
              const estado = a.isCurrent ? 'Vigente' : a.revokedAt ? 'Revocada' : 'Histórica'
              const estadoColor = a.isCurrent ? '#2a8a87' : a.revokedAt ? '#c8442f' : colors.textMuted
              return (
                <Box as="tr" key={a.id} borderTop="1px solid" borderColor={colors.border}>
                  <Box as="td" py={2} pr={3} fontWeight="700">v{a.version}</Box>
                  <Box as="td" py={2} pr={3} fontFamily="monospace" fontSize="xs">{a.cuidCode}</Box>
                  <Box as="td" py={2} pr={3}>{triggerLabel}</Box>
                  <Box as="td" py={2} pr={3} fontSize="xs">{formatDateTime(a.authorizedAt)}</Box>
                  <Box as="td" py={2} pr={3} fontSize="xs">{formatDate(a.expiresAt)}</Box>
                  <Box as="td" py={2} pr={3} color={estadoColor} fontWeight="600">{estado}</Box>
                  <Box as="td" py={2} pr={3} fontSize="xs">{a.authorizedByUserName ?? '-'}</Box>
                  <Box as="td" py={2}>
                    <Flex gap={2}>
                      {a.certificateUrl && <Box as="a" href={apiClient.buildUrl(`/proveedores/certificate-download?objectName=${encodeURIComponent(a.certificateUrl)}`)} target="_blank" fontSize="xs" color={brand.green} _hover={{ textDecoration: 'underline' }}>Cert</Box>}
                      {a.expedienteUrl && <Box as="a" href={apiClient.buildUrl(`/proveedores/certificate-download?objectName=${encodeURIComponent(a.expedienteUrl)}`)} target="_blank" fontSize="xs" color={brand.green} _hover={{ textDecoration: 'underline' }}>Expediente</Box>}
                    </Flex>
                  </Box>
                </Box>
              )
            })}
          </Box>
        </Box>
      </Box>
    </SectionCard>
  )
}
